import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { CoinUnitPriceType, fetchCoinUnitPrices, updateCoinUnitPrices } from '../../services/api/CoinUnitPrice';
import { ICoinUnitPrice } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  AllCoinUnitPrice = 'AllCoinUnitPrice',
}

interface IUseCoinUnitPriceMutation {
  update: UseMutationResult<ICoinUnitPrice, Error, ICoinUnitPrice>;
}

export const useCoinUnitPrices = (type: CoinUnitPriceType, year?: number): UseQueryResult<ICoinUnitPrice[], AxiosError> =>
  useQuery<ICoinUnitPrice[], AxiosError>(
    [QueryKey.AllCoinUnitPrice, { type, year }],
    async () => {
      const [fetchPromise] = fetchCoinUnitPrices(type, year);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    },
    {
      onError: e => {
        console.log(e.response ? e.response.data.message : e.message);
      },
      keepPreviousData: true,
    },
  );

export const useCoinUnitPricesMutation = (): IUseCoinUnitPriceMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const update = useMutation<ICoinUnitPrice, AxiosError, ICoinUnitPrice>(
    async (payload: ICoinUnitPrice) => {
      backdrop.show();
      const { data: apiResult } = await updateCoinUnitPrices(payload);

      if (!apiResult.success) {
        throw new Error(apiResult.errorCode);
      }

      return apiResult.data;
    },
    {
      onSuccess: () => {
        snackbar.success('Updated successfully.');
        queryClient.invalidateQueries(QueryKey.AllCoinUnitPrice);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return { update };
};
